import { Navigate, Outlet } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { LoadingSpinner } from "@/components/ui/LoadingSpinner";
import { Header } from "@/components/layout/Header";
import { useAutoLinkInviteCode } from "@/hooks/useAutoLinkInviteCode";
import { pendingAssessmentRequestPath } from "@/lib/pending-assessment-request";

/**
 * Layout for authenticated app pages. A user with a pending assessment request
 * (stashed at sign-up from an investor's invite) is sent to that request first,
 * whichever page sign-up landed them on (sv-3n86).
 * @returns The protected app shell, a redirect, or a loading state.
 * @throws Never.
 */
export function ProtectedLayout() {
  const { isAuthenticated, isLoading, user } = useAuth();
  useAutoLinkInviteCode();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <LoadingSpinner size="lg" label="Loading..." />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/sign-in" replace />;
  }

  const pendingRequestPath = pendingAssessmentRequestPath(user?.email);
  if (pendingRequestPath) {
    return <Navigate to={pendingRequestPath} replace />;
  }

  return (
    <div className="h-screen bg-background flex flex-col overflow-hidden">
      <Header />
      <main className="flex flex-col flex-1 min-h-0 overflow-hidden">
        <Outlet />
      </main>
    </div>
  );
}
